import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToneBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WORK_ORDER_STATUSES, PRIORITIES } from "@/lib/constants";
import { Wrench, Plus, Play, Pause, CheckCircle2, Trash2 } from "lucide-react";

const OPERATIVE_ROLES = ["tecnico", "mecanico", "hojalatero"];

export function WorkOrdersPanel({ serviceOrder, workOrders, workTasks, users, onChanged }) {
  const [creating, setCreating] = useState(false);
  const empty = {
    title: "",
    description: "",
    assigned_technician_id: "",
    priority: "normal",
    estimated_hours: "",
    actual_hours: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({}); // {wo_id: text}

  const [fieldErrors, setFieldErrors] = useState({});

  async function createWO() {
    if (saving) return;
    const errs = {};
    if (!form.title.trim()) errs.title = "El título es obligatorio";
    const hoursRaw = String(form.estimated_hours ?? "").trim();
    const hours = hoursRaw === "" ? null : Number(hoursRaw);
    if (hoursRaw !== "" && (!Number.isFinite(hours) || hours < 0 || hours > 1000)) {
      errs.estimated_hours = "Horas estimadas: escribe un número entre 0 y 1000 (por ejemplo 1.5)";
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) return toast.error(Object.values(errs)[0]);
    setSaving(true);
    try {
      // Explicit contract: optional fields go as null, never "" (actual_hours is not captured here).
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        assigned_technician_id: form.assigned_technician_id || null,
        priority: form.priority || "normal",
        estimated_hours: hours,
      };
      await api.post(`/service-orders/${serviceOrder.id}/work-orders`, payload);
      toast.success("Orden de trabajo creada");
      setForm(empty);
      setCreating(false);
      onChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const labels = { title: "Título", estimated_hours: "Horas estimadas", assigned_technician_id: "Técnico", priority: "Prioridad" };
        const mapped = {};
        detail.forEach((d) => { const f = d?.loc?.[d.loc.length - 1]; if (f) mapped[f] = `${labels[f] || f}: valor no válido`; });
        setFieldErrors(mapped);
        toast.error(Object.values(mapped)[0] || "Revisa los datos del formulario");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(wo, work_status) {
    try {
      await api.patch(`/service-orders/${serviceOrder.id}/work-orders/${wo.id}`, { work_status });
      toast.success("Estatus actualizado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deleteWO(wo) {
    if (!window.confirm(`¿Eliminar orden de trabajo "${wo.title}"?`)) return;
    try {
      await api.delete(`/service-orders/${serviceOrder.id}/work-orders/${wo.id}`);
      toast.success("Orden de trabajo eliminada");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function addTask(wo) {
    const title = (newTask[wo.id] || "").trim();
    if (!title) return;
    try {
      await api.post(`/service-orders/${serviceOrder.id}/work-orders/${wo.id}/tasks`, { title });
      setNewTask({ ...newTask, [wo.id]: "" });
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function toggleTask(wo, task) {
    try {
      await api.patch(
        `/service-orders/${serviceOrder.id}/work-orders/${wo.id}/tasks/${task.id}`,
        { is_completed: !task.is_completed }
      );
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deleteTask(wo, task) {
    try {
      await api.delete(`/service-orders/${serviceOrder.id}/work-orders/${wo.id}/tasks/${task.id}`);
      onChanged?.();
    } catch (_) {}
  }

  return (
    <div className="space-y-4" data-testid="panel-work-orders">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg tracking-tight">Órdenes de trabajo</h3>
          <p className="text-xs text-muted-foreground">
            Cada trabajo técnico asignado a un mecánico, con tareas checklist.
          </p>
        </div>
        {!creating && (
          <Button className="btn-tenant" size="sm" onClick={() => setCreating(true)} data-testid="btn-new-wo">
            <Plus className="h-4 w-4 mr-2" /> Nueva orden de trabajo
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-5 border-border space-y-4" data-testid="wo-form">
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Servicio de frenos delanteros" data-testid="wo-title" />
            {fieldErrors.title && <p className="text-xs text-red-600" data-testid="wo-title-error">{fieldErrors.title}</p>}
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="wo-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Técnico asignado</Label>
              <Select value={form.assigned_technician_id} onValueChange={(v) => setForm({ ...form, assigned_technician_id: v })}>
                <SelectTrigger data-testid="wo-technician"><SelectValue placeholder="Selecciona técnico" /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => OPERATIVE_ROLES.includes(u.role_name) && u.status === "active"
                    && [u.branch_id, ...(u.branch_ids || [])].includes(serviceOrder.branch_id)).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name} · {u.role_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="wo-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Horas estimadas</Label>
              <Input type="number" step="0.1" min="0" inputMode="decimal" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} data-testid="wo-estimated" />
              {fieldErrors.estimated_hours && <p className="text-xs text-red-600" data-testid="wo-estimated-error">{fieldErrors.estimated_hours}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)} data-testid="wo-cancel">Cancelar</Button>
            <Button onClick={createWO} disabled={saving} className="btn-tenant" data-testid="wo-save">
              {saving ? "Guardando…" : "Crear orden de trabajo"}
            </Button>
          </div>
        </Card>
      )}

      {workOrders.length === 0 && !creating && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-8 text-center bg-white">
          <Wrench className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          Sin órdenes de trabajo todavía.
        </div>
      )}

      {workOrders.map((wo) => {
        const tasks = workTasks.filter((t) => t.work_order_id === wo.id);
        const completed = tasks.filter((t) => t.is_completed).length;
        const tech = users.find((u) => u.id === wo.assigned_technician_id);
        return (
          <Card key={wo.id} className="border-border p-4" data-testid={`wo-${wo.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <ToneBadge list={WORK_ORDER_STATUSES} value={wo.work_status} />
                  <ToneBadge list={PRIORITIES} value={wo.priority} />
                </div>
                <div className="font-semibold text-base">{wo.title}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {tech ? `Técnico: ${tech.full_name}` : "Sin técnico asignado"}
                  {wo.estimated_hours ? ` · Est. ${wo.estimated_hours}h` : ""}
                  {wo.actual_hours ? ` · Real ${wo.actual_hours}h` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {wo.work_status === "pending" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(wo, "in_progress")} data-testid={`wo-start-${wo.id}`}>
                    <Play className="h-4 w-4 mr-1" /> Iniciar
                  </Button>
                )}
                {wo.work_status === "in_progress" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(wo, "paused")} data-testid={`wo-pause-${wo.id}`}>
                      <Pause className="h-4 w-4 mr-1" /> Pausar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(wo, "completed")} data-testid={`wo-complete-${wo.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Completar
                    </Button>
                  </>
                )}
                {wo.work_status === "paused" && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(wo, "in_progress")}>
                    <Play className="h-4 w-4 mr-1" /> Reanudar
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => deleteWO(wo)} data-testid={`wo-delete-${wo.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>

            {wo.description && (
              <p className="text-sm text-slate-700 mb-3">{wo.description}</p>
            )}

            <div className="border-t border-border pt-3 mt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Tareas ({completed}/{tasks.length})
                </div>
              </div>
              <ul className="space-y-1.5">
                {tasks.map((t) => (
                  <li key={t.id} className="flex items-start gap-2 group">
                    <Checkbox
                      checked={t.is_completed}
                      onCheckedChange={() => toggleTask(wo, t)}
                      data-testid={`task-check-${t.id}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${t.is_completed ? "line-through text-muted-foreground" : ""}`}>
                        {t.title}
                      </div>
                      {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                    </div>
                    <button
                      onClick={() => deleteTask(wo, t)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600 text-xs"
                      data-testid={`task-delete-${t.id}`}
                    >
                      Eliminar
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={newTask[wo.id] || ""}
                  onChange={(e) => setNewTask({ ...newTask, [wo.id]: e.target.value })}
                  placeholder="Agregar tarea…"
                  className="h-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addTask(wo); }
                  }}
                  data-testid={`task-input-${wo.id}`}
                />
                <Button size="sm" variant="outline" onClick={() => addTask(wo)} data-testid={`task-add-${wo.id}`}>
                  Agregar
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
